import { useState } from "react";
import { Link, useParams } from "react-router";
import { Aviso, Botao } from "../componentes/ui";
import { mensagemDoConsole } from "../nucleo/console";
import { ROTULO_DA_COR, useChamadoParaImpressao, type CorDoSinalizador } from "../nucleo/operacao";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { ROTULO_DA_URGENCIA, ROTULO_DO_ESTADO, rotuloDoCanal } from "../nucleo/tickets";
import { useTitulo } from "../nucleo/titulo";

function quando(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/**
 * A página de impressão do chamado: só o chamado, sem a casca do portal. Quem é da equipe e pode
 * ver nota interna escolhe imprimir com ou sem elas (o padrão é sem); para o cliente a opção nem
 * existe — e o servidor ignora o pedido de nota interna de quem não pode.
 */
export function ImpressaoDoChamado({ interno = false }: { interno?: boolean }) {
  const { id = "" } = useParams();
  useTitulo("Imprimir chamado");
  const sessao = useSessao();
  const [comNotas, setComNotas] = useState(false);
  const podeEscolher = interno && podeNaSessao(sessao.data, "ticket:nota_interna");
  const chamado = useChamadoParaImpressao(id, podeEscolher && comNotas);
  const voltar = `${interno ? "/admin" : "/app"}/chamados/${id}`;

  if (chamado.isPending) return <p role="status">Preparando a impressão…</p>;
  if (chamado.isError) return <Aviso tom="erro">{mensagemDoConsole(chamado.error)}</Aviso>;
  const item = chamado.data;

  return (
    <article className="impressao" data-testid="impressao-do-chamado">
      <div className="nao-imprimir mb-4 flex flex-wrap items-center gap-3">
        <Botao onClick={() => window.print()}>Imprimir</Botao>
        <Link className="botao botao--fantasma" to={voltar}>Voltar ao chamado</Link>
        {podeEscolher ? (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={comNotas}
              onChange={(evento) => setComNotas(evento.target.checked)}
            />
            Incluir notas internas
          </label>
        ) : null}
      </div>

      <p className="nota-rodape">Chamado #{item.numero} · {item.organizacao}</p>
      <h1 className="titulo-pagina">{item.assunto}</h1>
      {item.com_notas_internas ? (
        <p className="impressao__item" data-testid="aviso-de-notas-internas">
          <strong>Contém notas internas. Não entregue ao cliente.</strong>
        </p>
      ) : null}

      <dl className="impressao__item grid gap-1 sm:grid-cols-2">
        <div><dt className="nota-rodape">Estado</dt><dd>{ROTULO_DO_ESTADO[item.estado as keyof typeof ROTULO_DO_ESTADO] ?? item.estado}</dd></div>
        <div><dt className="nota-rodape">Urgência</dt><dd>{ROTULO_DA_URGENCIA[item.urgencia as keyof typeof ROTULO_DA_URGENCIA] ?? item.urgencia}</dd></div>
        <div><dt className="nota-rodape">Aberto em</dt><dd>{quando(item.criado_em)}</dd></div>
        <div><dt className="nota-rodape">Resolvido em</dt><dd>{quando(item.resolvido_em)}</dd></div>
        {item.solicitante ? <div><dt className="nota-rodape">Solicitante</dt><dd>{item.solicitante}</dd></div> : null}
        {item.responsavel ? <div><dt className="nota-rodape">Responsável</dt><dd>{item.responsavel}</dd></div> : null}
        {item.equipe ? <div><dt className="nota-rodape">Equipe</dt><dd>{item.equipe}</dd></div> : null}
        {item.categoria ? <div><dt className="nota-rodape">Categoria</dt><dd>{item.categoria}</dd></div> : null}
        {item.servico ? <div><dt className="nota-rodape">Serviço</dt><dd>{item.servico}</dd></div> : null}
        {item.campos.map(([rotulo, valor]) => (
          <div key={rotulo}><dt className="nota-rodape">{rotulo}</dt><dd>{valor}</dd></div>
        ))}
        {item.sinalizador ? (
          <div>
            <dt className="nota-rodape">Sinalizador</dt>
            <dd>{ROTULO_DA_COR[item.sinalizador.cor as CorDoSinalizador]} · {item.sinalizador.motivo}</dd>
          </div>
        ) : null}
      </dl>

      <section className="impressao__item">
        <h2 className="titulo-secao">Solicitação</h2>
        <p className="texto-preservado">{item.descricao}</p>
      </section>

      <section aria-labelledby="titulo-conversa-impressa">
        <h2 className="titulo-secao" id="titulo-conversa-impressa">Conversa</h2>
        {item.acoes.length === 0 ? <p className="nota-rodape">Sem respostas neste chamado.</p> : null}
        {item.acoes.map((acao) => (
          <div className="impressao__item" key={acao.id}>
            <strong>{acao.tipo === "interna" ? "Nota interna" : acao.autor_email ?? "Equipe GEOvendas"}</strong>
            <span className="nota-rodape">
              {" · "}{quando(acao.criada_em)}
              {rotuloDoCanal(acao.canal) ? ` · ${rotuloDoCanal(acao.canal)}` : ""}
            </span>
            <p className="texto-preservado">{acao.texto}</p>
          </div>
        ))}
      </section>

      {item.anexos.length > 0 ? (
        <section className="impressao__item">
          <h2 className="titulo-secao">Arquivos anexados</h2>
          <ul>{item.anexos.map((anexo) => <li key={anexo.id}>{anexo.nome}</li>)}</ul>
        </section>
      ) : null}

      <p className="nota-rodape">Impresso por {item.impresso_por} em {quando(item.impresso_em)}.</p>
    </article>
  );
}
